import { useSecureFederatedMutation as useMutation } from '@at/sauron-platform-graphql';
import { ConversationGql } from './query/conversation.fed.query';
import { useEffect, useState } from 'react';
export const useLoginToZendesk = ({ isUserSignedIn }) => {
    // The setting 'ignoreResults: true' in the useMutation hook is intentional.
    // It ensures that the data property, which would typically hold the mutation result, doesn't trigger updates.
    // This decision was made to optimise the performance of the parent component by minimizing unnecessary re-renders.
    // Instead of relying on automatic updates to the data property, we handle the mutation result directly within the
    // useEffect hook, allowing for more explicit control over the rendering process.
    const [generateZendeskJwt] = useMutation(ConversationGql, { ignoreResults: true });
    const [zendeskAuthed, setZendeskAuthed] = useState(false);
    useEffect(() => {
        function registerAuthJwtCallbackWithZendeskAndLogSignedInUserInToZendeskChat() {
            // https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/
            window.zE('messenger', 'loginUser', function (registerNewJwtCallback) {
                generateZendeskJwt()
                    .then((response) => {
                    var _a, _b;
                    if (response.errors) {
                        console.error('Failed to generate Jwt for Zendesk Chat.');
                        return;
                    }
                    const jwtToken = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.conversation) === null || _b === void 0 ? void 0 : _b.initiateChat.jwtToken;
                    if (!jwtToken) {
                        console.error('There were no errors but no Jwt token was found in the response.');
                        return;
                    }
                    registerNewJwtCallback(jwtToken);
                    setZendeskAuthed(true);
                })
                    .catch(() => {
                    console.error('Something went wrong when trying to generate a Jwt for Zendesk Chat.');
                });
            });
        }
        function logUserIntoZendeskIfUserIsSignedInAndUserHasNotBeenSignedIntoZendeskYet() {
            if (isUserSignedIn && !zendeskAuthed) {
                registerAuthJwtCallbackWithZendeskAndLogSignedInUserInToZendeskChat();
            }
        }
        // Regularly check to see if Zendesk Embeddable has successfully been attached to the global Window object
        // When it is, perform a login if it is required
        // Give up on this after a timout
        const waitForZendeskScriptToBeReady_thenLogInToZendeskIfRequired = setInterval(() => {
            // If 'zE' (Zendesk Embeddable) has successfully been attached to the window object, exposing the API through window.zE,
            // this confirms that we can interact with Zendesk Chat API features.
            if (window.zE) {
                logUserIntoZendeskIfUserIsSignedInAndUserHasNotBeenSignedIntoZendeskYet();
                clearInterval(waitForZendeskScriptToBeReady_thenLogInToZendeskIfRequired);
            }
        }, 200);
        // we wait a maximum of 8 seconds for the zendesk script to be ready before clearing the regular interval
        // 8 seconds is the max we wait because during testing the script was always ready within the first 400ms in the office on good internet!
        // Although 8 seconds may have been set arbitrarily and will look at refining it but for those on slower internet connections
        // it may need to be even longer!
        const waitForZendeskScriptToBeReadyTimeout = setTimeout(() => {
            clearInterval(waitForZendeskScriptToBeReady_thenLogInToZendeskIfRequired);
        }, 8000);
        // If our hook needs to perform cleanup of the above interval and timeout when the component unmounts, we use
        // the useEffect hook inside the custom hook so the cleanup function of useEffect is executed when the component unmounts.
        return () => {
            clearInterval(waitForZendeskScriptToBeReady_thenLogInToZendeskIfRequired);
            clearTimeout(waitForZendeskScriptToBeReadyTimeout);
        };
    }, [generateZendeskJwt, isUserSignedIn, zendeskAuthed]);
};
